<!-- Plan Modal -->
<script setup lang="ts">
import {
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonTitle,
  IonToolbar,
  modalController,
} from '@ionic/vue';
import { inject, ref, onMounted, computed } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import { closeOutline, warningOutline } from 'ionicons/icons';
import BPlanItem from '@/components/BPlanItem.vue';
import LifestyleResult from '@/components/results/LifestyleResult.vue';
import WorkResult from '@/components/results/WorkResult.vue';
import { FmsType } from '@/types';

const props = defineProps<{
  profile: { id: number; created_at: string };
  plan: any;
}>();

const fmsType = inject('fms-type') as FmsType;
const isLoading = ref(true);
const visibleSections = ref({
  work: false,
  lifestyle: false,
  goals: false
});
const formattedDate = computed(() => 
  dayjs(props.profile.created_at).locale('sv').format('D MMMM, YYYY')
);

// Cache computed values
const hasGoals = computed(() => 
  props.plan.goals?.length > 0
);
const hasOldGoals = computed(() => 
  hasGoals.value && props.plan.goals[0].texts?.length
);
const workQuestions = computed(() => {
  // console.log(fmsType);
  // console.log(props.plan.questions.school?.groups[0]);
  
  // if (!fmsType) return []; //check if fmsType is null or undefined
  // fmsType.value === FmsType.School 
  return   props.plan.questions.school?.groups[0]?.questions 
    // : props.plan.questions.work?.groups[0]?.questions;
});
const lifestyleSections = computed(() => 
  props.plan.questions.life?.sections
);
const profileFactors = computed(() => 
  props.plan.profile?.factors
);

// Optimization: Progressive loading
onMounted(() => {
  isLoading.value = false;
  
  // Progressive section loading
  setTimeout(() => { visibleSections.value.work = true; }, 50);
  setTimeout(() => { visibleSections.value.lifestyle = true; }, 150);
  setTimeout(() => { visibleSections.value.goals = true; }, 250);
});

function close() {
  modalController.dismiss();
}
</script>

<template>
  <ion-content class="modal-content">
    <!-- Fixed Header -->
    <ion-toolbar class="modal-header">
      <ion-title>
        <div class="title-text">Livsstilsplan</div>
        <div class="date-text">{{ formattedDate }}</div>
      </ion-title>
      <ion-buttons slot="end">
        <ion-button @click="close" class="close-button">
          <ion-icon :icon="closeOutline" size="large" />
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
    
    <!-- Loading Indicator -->
    <div v-if="isLoading" class="loading-container">
      <div class="loader"></div>
      <p>Laddar din plan...</p>
    </div>
    
    <!-- Main Content -->
    <div v-else class="content-container">
      <div class="content-wrapper">
        <!-- Warning for Old Plans -->
        <div v-if="hasOldGoals" class="warning-banner">
          <ion-icon :icon="warningOutline" class="warning-icon" />
          <p>Detta är en gammal livsstilsplan! Innehållet nedan kan därför se lite konstigt ut.</p>
        </div>
        
        <!-- Work/School Section -->
        <section v-if="visibleSections.work" class="plan-section">
          <h2 class="section-title">
           Min skolgång 
          </h2>
          <WorkResult
            v-if="workQuestions && profileFactors"
            :questions="workQuestions"
            :factors="profileFactors"
          />
        </section>
        
        <!-- Lifestyle Section -->
        <section v-if="visibleSections.lifestyle" class="plan-section">
          <h2 class="section-title">Min livsstil</h2>
          <LifestyleResult
            v-if="lifestyleSections && profileFactors"
            :sections="lifestyleSections"
            :factors="profileFactors"
          />
        </section>
        
        <!-- Goals Section -->
        <section v-if="visibleSections.goals" class="plan-section">
          <h2 class="section-title">Dina mål</h2>
          <ul v-if="hasGoals" class="goals-list uniform-goals goals-grid">
            <b-plan-item
              v-for="(goal, index) in plan.goals"
              :key="index"
              :data="goal"
              class="goal-item"
            >
              {{ goal.label }}
            </b-plan-item>
          </ul>
          <div v-else class="no-goals">
            Du har inte valt något att förbättra.
          </div>
        </section>
      </div>
    </div>
  </ion-content>
</template>

<style scoped>
/* Base Styles */
.modal-content {
  --background: theme('colors.yellow.light');
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-start: 0;
  --padding-end: 0;
  --keyboard-offset: 0;
  contain: size style;
}

.modal-header {
  --background: theme('colors.yellow.light');
  --color: theme('colors.yellow.darker');
  --min-height: 64px;
  --border-color: transparent;
  position: sticky;
  top: 0;
  z-index: 10;
  will-change: transform;
  contain: layout style;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.title-text {
  font-size: 20px;
  font-weight: 600;
  will-change: transform;
  contain: content;
}

.date-text {
  font-size: 14px;
  opacity: 0.8;
  will-change: transform;
  contain: content;
}

.close-button {
  --color: theme('colors.yellow.darker');
  --background: transparent;
  --border-radius: 50%;
  --padding-start: 12px;
  --padding-end: 12px;
  --padding-top: 12px;
  --padding-bottom: 12px;
  font-size: 24px;
  transition: opacity 0.2s ease;
  display: flex !important;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 20;
}

.close-button:hover {
  opacity: 0.8;
}

ion-icon {
  pointer-events: none;
}

/* Loader Styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100% - 64px);
  padding: 2rem;
  color: theme('colors.yellow.darker');
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: theme('colors.yellow.darker');
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
  contain: strict;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Content Styles */
.content-container {
  position: absolute;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #FFFBEE;
  overscroll-behavior: contain;
  contain: content;
  will-change: transform;

  /* Optimize scrolling */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.content-wrapper {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  contain: content;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 2rem;
  background-color: theme('colors.purple.dark');
  color: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transform: translateZ(0);
  will-change: transform;
  contain: content;
}

.warning-icon {
  font-size: 1.75rem;
  flex-shrink: 0;
}

.warning-banner p {
  margin: 0;
  line-height: 1.4;
  font-size: 0.95rem;
}

/* Section Styles */
.plan-section {
  margin-bottom: 2.5rem;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeIn 0.3s ease forwards;
  will-change: transform, opacity;
  contain: content;
}

@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-title {
  margin: 0 0 1.5rem;
  font-size: 1.5rem;
  font-weight: normal;
  color: theme('colors.yellow.darker');
  text-align: center;
  contain: content;
}

/* Goals List */
.goals-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
  contain: content;
}

.goal-item {
  width: 100%;
  max-width: 500px;
  contain: content;
  transform: translateZ(0);
  will-change: transform;
}

.uniform-goals .goal-item { 
  max-width: 100%; 
}

.goals-grid { 
  grid-template-columns: 1fr; 
}

@media (min-width: 768px) {
  .goals-grid { 
    grid-template-columns: 1fr 1fr; 
  }
}

.no-goals {
  text-align: center;
  color: #666;
  padding: 1rem;
}

/* Responsive Adjustments */
@media (min-width: 640px) {
  .content-wrapper {
    padding: 2rem 3rem;
  }
  
  .warning-banner {
    padding: 1.25rem;
  }
}

@media (min-width: 1024px) {
  .content-wrapper {
    padding: 2.5rem 4rem;
  }
  
  .warning-banner p {
    font-size: 1rem;
  }
  
  .section-title {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
}

/* Performance Optimizations for Low-End Devices */
@media (prefers-reduced-motion: reduce) {
  .plan-section {
    animation: none;
    opacity: 1;
    transform: none;
  }
  
  .loader {
    animation-duration: 1.5s;
  }
}

/* Print Styles */
@media print {
  .modal-header, .close-button {
    display: none;
  }
  
  .content-container {
    position: static;
    overflow: visible;
  }
  
  .warning-banner {
    border: 1px solid #666;
  }
}
</style>